import { Box, Grid, GridProps, LoadingOverlay, Overlay, Progress } from '@mantine/core';
import { flexRender, Table } from '@tanstack/react-table';
import { Row } from '@tanstack/table-core';
import { ComponentType, ReactElement, ReactNode, Ref } from 'react';
import { TableSkeleton } from '@/app/assets/components/table/Skeletons';

export type Disableable = {
  disabled?: boolean;
};

type StyleClasses = {
  table?: string;
  tableRow?: string;
  tableHeaderRow?: string;
  tableRowExpanded?: string;
  tableRowExpandedInner?: string;
  selectedRow?: string;
  selectedExpandedRow?: string;
  hoverRow?: string;
  editedRow?: string;
};

type ExpandableTableProps<TRow extends Disableable> = {
  columnsSpan: number;
  table: Table<TRow>;
  nestedTable?: Table<TRow>;
  rowAlign?: GridProps['align'];
  rootGridProps?: GridProps;
  renderSubComponent?: (props: { row: Row<TRow> }) => ReactElement;
  focusedRow?: Row<TRow>;
  setFocusedRow?: (row: Row<TRow> | undefined) => void;
  mRef?: Ref<HTMLDivElement>;
  rowDetailsComponent?: ComponentType<{ id: string }>;
  styleClasses?: StyleClasses;
  getRowId?: (row: Row<TRow>) => string;
  RowProvider?: ComponentType<{ children: ReactNode; value: any }>;
  isLoading?: boolean;
  isFetching?: boolean;
};

export const WrapIfExists = ({
  children,
  Wrapper,
  wrapperProps,
}: {
  children: ReactNode;
  Wrapper?: ComponentType<{ children: ReactNode; value: any }>;
  wrapperProps: { value: any };
}) => {
  if (Wrapper) {
    return <Wrapper value={wrapperProps.value}>{children}</Wrapper>;
  }
  return children;
};

export function ExpandableTable<TRow extends Disableable>({
  table,
  nestedTable,
  columnsSpan,
  rowAlign = 'center',
  rootGridProps,
  renderSubComponent,
  focusedRow,
  setFocusedRow,
  mRef,
  rowDetailsComponent: RowDetailsComponent,
  styleClasses,
  getRowId,
  RowProvider,
  isLoading,
  isFetching,
}: ExpandableTableProps<TRow>) {
  if (isLoading) {
    return <TableSkeleton />;
  }
  return (
    <>
      <Box mih={8}>{isFetching ? <Progress value={100} animated /> : null}</Box>
      <Grid
        ref={mRef}
        className={styleClasses?.table}
        columns={columnsSpan}
        maw={1440}
        miw={1100}
        pos='relative'
        justify='flex-end'
        {...rootGridProps}
      >
        <Grid.Col key='header' pos='relative' span={columnsSpan}>
          <Grid align={rowAlign} columns={columnsSpan} className={styleClasses?.tableHeaderRow}>
            {table.getFlatHeaders().map((header) =>
              header.isPlaceholder ? null : (
                <Grid.Col
                  className={header.column.columnDef.meta?.headerRowClassName}
                  span={header.column.columnDef.meta?.span}
                  key={header.id}
                  px='xs'
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Grid.Col>
              )
            )}
          </Grid>
        </Grid.Col>
        {nestedTable &&
          nestedTable.getRowModel().rows.map((row) => (
            <Grid.Col
              key={row.id}
              pos='relative'
              span={columnsSpan}
              className={`${row.depth > 0 ? styleClasses?.tableRowExpanded : styleClasses?.tableRow} ${styleClasses?.hoverRow}`}
            >
              <Grid align={rowAlign} columns={columnsSpan} py='xs' pl={6}>
                {row.getVisibleCells().map((cell) => (
                  <Grid.Col
                    span={cell.column.columnDef.meta?.span}
                    key={cell.id}
                    className={cell.column.columnDef.meta?.rowClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Grid.Col>
                ))}
              </Grid>
            </Grid.Col>
          ))}
        {table.getRowModel().rows.map((row) => (
          <WrapIfExists key={row.id} wrapperProps={{ value: row.original }} Wrapper={RowProvider}>
            <Grid.Col
              pos='relative'
              key={row.id}
              data-row-id={(row.original as any).name as any}
              span={columnsSpan}
              className={`${(row.depth > 0 ? styleClasses?.tableRowExpanded : styleClasses?.tableRow) ?? ''}
          ${row.id === focusedRow?.id && row.depth === 0 ? styleClasses?.selectedRow : ''}
          ${row.depth === 0 && row.id !== focusedRow?.id ? styleClasses?.hoverRow : ''}
          ${styleClasses?.editedRow}`}
              tabIndex={0}
              py='xs'
              id={getRowId?.(row) || row.id}
            >
              {RowDetailsComponent && row.depth === 0 && <RowDetailsComponent id={row.id} />}
              {row.original.disabled && <Overlay zIndex={180} opacity={0.6} c='gray.0' bg='gray.0' />}
              <Grid
                align={rowAlign}
                columns={columnsSpan}
                className={`${row.depth === 2 ? styleClasses?.tableRowExpandedInner : ''}
            ${
              row.id === focusedRow?.id && row.depth > 0 ? styleClasses?.selectedExpandedRow : ''
            } ${row.depth === 2 && row.id !== focusedRow?.id ? styleClasses?.hoverRow : ''}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <Grid.Col
                    pos='relative'
                    className={`${cell.column.columnDef.meta?.rowClassName}`}
                    style={{
                      cursor: cell.column.columnDef.meta?.handleOnClick ? 'pointer' : '',
                    }}
                    span={cell.column.columnDef.meta?.span}
                    key={cell.id}
                    onClick={cell.column.columnDef.meta?.handleOnClick ? () => setFocusedRow?.(row) : () => {}}
                    px='4'
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Grid.Col>
                ))}
                {renderSubComponent && row.getIsExpanded() && (
                  <Grid.Col span={columnsSpan} p={0} m={0}>
                    {renderSubComponent({ row })}
                  </Grid.Col>
                )}
              </Grid>
            </Grid.Col>
          </WrapIfExists>
        ))}
      </Grid>
    </>
  );
}
